import { useState, useEffect } from 'react';
import ConfirmModal from './ConfirmModal';
import CategoryUsageModal from './CategoryUsageModal';
import HistoryTimeline from './HistoryTimeline';
import { APIClient } from "../helpers/api_helper";
import { CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import { Alert, Button, Col, Drawer, Form, Input, Row, Segmented, Space } from 'antd';

const api = new APIClient();


const CategoryDrawer = (props) => {
  const [form] = Form.useForm();
  const [activeDrawerTab, setActiveDrawerTab] = useState('data');
  const [showDeletionConfirmModal, setShowDeletionConfirmModal] = useState(false);
  const [data, setData] = useState(null);
  // Categories are shared org-wide: deleting one cascades into every project
  // budget that uses it, and renaming one relabels the section everywhere.
  const [usage, setUsage] = useState(null);
  const [usageModalMode, setUsageModalMode] = useState(null); // 'blocked' | 'rename'
  const [pendingValues, setPendingValues] = useState(null);

  const loadUsage = () => api.get(`/api/categories/${props.id}/usage/`, {}).then(resp => {
    setUsage(resp);
    return resp;
  });

  useEffect(() => {
    setActiveDrawerTab('data');
    setUsageModalMode(null);
    setPendingValues(null);
    if (props.id) {
      api.get(`/api/categories/${props.id}/`, {})
        .then(resp => {
          setData(resp);
          form.setFieldsValue(resp);
        });
      loadUsage();
    } else {
      form.resetFields();
      setData(null);
      setUsage(null);
    }
  }, [props.isOpen, props.id]);


  const handleCancel = () => {
    form.resetFields();
    props.onCancel()
  }

  const handleConfirmedDelete = () => {
    api.delete(`/api/categories/${props.id}/`, {}, {successMsg: `Kategorija uspešno izbrisana ${props.id}: ${data.title}`})
      .then(resp => {
        props.onDone()
        form.resetFields();
        setShowDeletionConfirmModal(false);
      })
      .catch(error => {
        // The backend refuses a category that is in use. It can also have come
        // into use since the drawer was opened, so re-read before explaining.
        console.error(error);
        setShowDeletionConfirmModal(false);
        loadUsage().then(() => setUsageModalMode('blocked'));
      })
  };

  const save = (values) => {
    const payload = { ...values };

    const isEdit = props.id !== null;
    const apiCall = isEdit
        ? api.update(`/api/categories/${props.id}/`, payload, {successMsg: `Kategorija uspešno shranjena ${payload.title}`})
        : api.create('/api/categories/', payload, {successMsg: `Kategorija uspešno ustvarjena ${payload.title}`});

    apiCall.then(resp => {
        props.onDone();
        form.resetFields();
      }).catch(err => console.error('API error:', err));
  };

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const titleChanged = !isCreation && data && values.title?.trim() !== data.title;
        if (titleChanged && usage?.in_use) {
          setPendingValues(values);
          setUsageModalMode('rename');
          return;
        }
        save(values);
      })
      .catch((info) => {
        console.error('Validate Failed:', info);
      });
  };

  const isCreation = props.id == null;
  const inUse = !!usage?.in_use;
  const nProjects = usage?.projects?.length || 0;

  return (
    <>
      <Form layout="vertical" form={form}>
        <Drawer
          title={
            <Space>
              <span>{isCreation ? "Ustvari novo kategorijo" : `Kategorija #${props.id}`}</span>
              {!isCreation && (
                <Segmented
                  size="small"
                  value={activeDrawerTab}
                  onChange={setActiveDrawerTab}
                  options={[
                    { label: 'Podatki', value: 'data' },
                    { label: 'Zgodovina', value: 'history' },
                  ]}
                />
              )}
            </Space>
          }
          width={560}
          onClose={handleCancel}
          open={props.open}
          extra={
            activeDrawerTab === 'data' && (
              <Space>
                <Button
                  disabled={isCreation}
                  onClick={() => inUse ? setUsageModalMode('blocked') : setShowDeletionConfirmModal(true)}
                  danger
                ><DeleteOutlined /></Button>
                <Button onClick={handleCancel}><CloseOutlined /></Button>
                <Button disabled={false} onClick={onSubmit} type="primary">Shrani</Button>
              </Space>
            )
          }
        >
          {activeDrawerTab === 'data' && (
            <Row gutter={16}>
              {inUse && (
                <Col span={24}>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={
                      nProjects > 0
                        ? `V uporabi v ${nProjects} ${nProjects === 1 ? 'projektu' : 'projektih'} - brisanje ni mogoče, sprememba naziva velja povsod.`
                        : 'Kategorija je v uporabi - brisanje ni mogoče.'
                    }
                  />
                </Col>
              )}
              <Col span={24}>
                <Form.Item
                  name="title"
                  label='Naziv'
                  rules={[{ required: true, message: 'Prosim vnesite naziv kategorije' }]}
                >
                  <Input placeholder={'naziv...'} />
                </Form.Item>
              </Col>
            </Row>
          )}

          {activeDrawerTab === 'history' && (
            <HistoryTimeline model="ProjectBudgetExpenseCategory" objectId={props.id} />
          )}
        </Drawer>
      </Form>

      <ConfirmModal
        textComponent={<p>{`Ali ste prepričani, da želite izbrisati kategorijo #${data?.id}: ${data?.title}?`}</p>}
        open={showDeletionConfirmModal}
        confirmBtnTitle='Izbriši'
        confirmBtnProps={{'danger': true}}
        onOk={() => handleConfirmedDelete()}
        onCancel={() => {
          setShowDeletionConfirmModal(false);
        }}
      />

      <CategoryUsageModal
        open={usageModalMode !== null}
        mode={usageModalMode}
        usage={usage}
        onOk={() => {
          setUsageModalMode(null);
          if (pendingValues) {
            save(pendingValues);
            setPendingValues(null);
          }
        }}
        onCancel={() => {
          setUsageModalMode(null);
          setPendingValues(null);
        }}
      />
    </>
  );
};

export default CategoryDrawer;
